import { apiClient } from './apiClient';
import type { BriefDetailType } from '../components/Profile/brief/types';

// Temporary mock backend for briefs, since real backend is still being wired, but we use apiClient wrapper
// In standard use case, apiClient forwards to the real backend.

export interface GetBriefsResponse {
    briefs: BriefDetailType[];
    totalCount: number;
    hasMore: boolean;
    cursor: string | null;
}

export interface GetBriefDetailResponse {
    success: boolean;
    data?: {
        brief: BriefDetailType;
        client: {
            userId: string;
            displayName: string;
            username: string;
            avatarUrl: string;
        };
    };
    error?: string;
}

export interface AIBriefGenerationRequest {
    coreGoal: string;
    contextAndAudience?: string;
    workType?: string;
    selectedStyleFacets?: string[];
    expectedDeliverables: string[];
    mustInclude?: string;
    referenceNotes?: string;
}

export interface AIBriefGenerationResponse {
    title: string;
    description: string;
    deliverables: string;
    tags: string[];
    primaryCategory?: string | null;
    secondaryCategories?: string[];
    styleFacets?: string[];
    toolTags?: string[];
}

export interface AIBriefGenerationApiResponse {
    success: boolean;
    data?: AIBriefGenerationResponse;
    error?: string;
}

export const briefService = {
    /**
     * Get a paginated list of briefs
     * Endpoint: GET /api/v1/briefs
     */
    getBriefs: async (params?: { clientUserId?: string; status?: 'draft' | 'active' | 'closed' | 'archived'; sort?: 'latest' | 'hot'; limit?: number; cursor?: string; includeArchived?: boolean }) => {
        let queryStr = '';
        if (params) {
            const queryParams = new URLSearchParams();
            if (params.clientUserId) queryParams.append('clientUserId', params.clientUserId);
            if (params.status) queryParams.append('status', params.status);
            if (params.sort) queryParams.append('sort', params.sort);
            if (params.limit) queryParams.append('limit', params.limit.toString());
            if (params.cursor) queryParams.append('cursor', params.cursor);
            if (params.includeArchived) queryParams.append('includeArchived', 'true');
            queryStr = `?${queryParams.toString()}`;
        }
        return apiClient.get<GetBriefsResponse>(`/api/v1/briefs${queryStr}`);
    },

    /**
     * Get a brief by ID
     * Endpoint: GET /api/v1/briefs/:briefId
     */
    getBriefById: async (briefId: string | number) => {
        return apiClient.get<GetBriefDetailResponse>(`/api/v1/briefs/${briefId}`);
    },

    /**
     * Create a new brief
     * Endpoint: POST /api/v1/briefs
     */
    createBrief: async (data: Omit<BriefDetailType, 'id' | 'clientUserId' | 'createdAt' | 'updatedAt'>) => {
        return apiClient.post<{ success: boolean; data?: BriefDetailType; error?: string }>('/api/v1/briefs', data);
    },

    /**
     * Update an existing brief
     * Endpoint: PUT /api/v1/briefs/:briefId
     */
    updateBrief: async (briefId: string | number, data: Partial<BriefDetailType>) => {
        return apiClient.put<{ success: boolean; data?: BriefDetailType; error?: string }>(`/api/v1/briefs/${briefId}`, data);
    },

    /**
     * Delete a brief
     * Endpoint: DELETE /api/v1/briefs/:briefId
     */
    deleteBrief: async (briefId: string | number) => {
        return apiClient.delete<{ success: boolean; message?: string; error?: string }>(`/api/v1/briefs/${briefId}`);
    },

    archiveBrief: async (briefId: string | number) => {
        return briefService.updateBrief(briefId, { status: 'archived' });
    },

    restoreBrief: async (briefId: string | number) => {
        return briefService.updateBrief(briefId, { status: 'active' });
    },

    reorderBriefs: async (briefIds: Array<string | number>) => {
        return apiClient.post<{ success: boolean; error?: string }>('/api/v1/briefs/reorder', {
            briefIds: briefIds.map(String)
        });
    },

    /**
     * Increment communication count for a brief
     * Endpoint: POST /api/v1/briefs/:briefId/communicate
     */
    incrementCommunicationCount: async (briefId: string | number) => {
        return apiClient.post<{ success: boolean; message?: string; error?: string }>(`/api/v1/briefs/${briefId}/communicate`);
    },

    /**
     * Increment trial count for a brief after a trial request is accepted
     * Endpoint: POST /api/v1/briefs/:briefId/trials
     */
    incrementTrialCount: async (briefId: string | number) => {
        return apiClient.post<{ success: boolean; message?: string; error?: string }>(`/api/v1/briefs/${briefId}/trials`);
    },

    /**
     * Increment view count when a brief detail is opened
     * Endpoint: POST /api/v1/briefs/:briefId/view
     */
    incrementViewCount: async (briefId: string | number) => {
        return apiClient.post<{ success: boolean; message?: string; error?: string }>(`/api/v1/briefs/${briefId}/view`);
    },

    /**
     * Generate brief fields via AI
     * Endpoint: POST /api/v1/briefs/ai-generate
     */
    generateAIBrief: async (data: AIBriefGenerationRequest) => {
        return apiClient.post<AIBriefGenerationApiResponse>('/api/v1/briefs/ai-generate', data);
    }
};
